import PropTypes from "prop-types";
import { useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import MaterialSelect from "./MaterialSelect";
import "../../style/QuickAddPanel.css";

const COLUMNS = ["material", "item_name", "quantity", "cost", "latest_pickup"];

const QuickAddGrid = ({ rows, setRows, materialTypes, errors, disabled, emptyRow }) => {
  const { t } = useTranslation();
  const gridRef = useRef(null);
  const today = new Date().toISOString().split("T")[0];

  const updateRow = useCallback((idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  }, [setRows]);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, emptyRow()]);
  }, [setRows, emptyRow]);

  const removeRow = useCallback((idx) => {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  }, [setRows]);

  const handleKeyDown = useCallback((e, rowIdx, colIdx) => {
    const isLast = rowIdx === rows.length - 1 && colIdx === COLUMNS.length - 1;
    if (isLast && (e.key === "Tab" && !e.shiftKey)) {
      e.preventDefault();
      addRow();
      setTimeout(() => {
        const inputs = gridRef.current?.querySelectorAll(`[data-row="${rowIdx + 1}"]`);
        inputs?.[0]?.focus();
      }, 50);
    }
  }, [rows.length, addRow]);

  return (
    <div className="quick-add-grid" ref={gridRef}>
      <table className="quick-add-table">
        <thead>
          <tr>
            <th className="qa-col-num">#</th>
            <th className="qa-col-material">{t("Material Type")} *</th>
            <th className="qa-col-name">{t("Item Name")}</th>
            <th className="qa-col-qty">{t("Qty (lbs)")} *</th>
            <th className="qa-col-cost">{t("Cost ($)")} *</th>
            <th className="qa-col-date">{t("Pickup Deadline")} *</th>
            <th className="qa-col-action" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const rowErr = errors[i] || {};
            return (
              <tr key={row.id} className={Object.keys(rowErr).length ? "qa-row-error" : ""}>
                <td className="qa-cell-num">{i + 1}</td>
                <td className="qa-cell-material">
                  <MaterialSelect
                    value={row.material}
                    onChange={(val) => updateRow(i, "material", val)}
                    options={materialTypes}
                    disabled={disabled}
                    hasError={!!rowErr.material}
                    compact
                  />
                  {rowErr.material && <span className="qa-error">{rowErr.material}</span>}
                </td>
                <td>
                  <input
                    data-row={i}
                    type="text"
                    className="qa-input"
                    value={row.item_name}
                    onChange={(e) => updateRow(i, "item_name", e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, i, 1)}
                    disabled={disabled}
                  />
                </td>
                <td>
                  <input
                    data-row={i}
                    type="number"
                    className={`qa-input ${rowErr.quantity ? "error" : ""}`}
                    value={row.quantity}
                    onChange={(e) => updateRow(i, "quantity", e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, i, 2)}
                    min="0"
                    step="any"
                    disabled={disabled}
                  />
                  {rowErr.quantity && <span className="qa-error">{rowErr.quantity}</span>}
                </td>
                <td>
                  <input
                    data-row={i}
                    type="number"
                    className={`qa-input ${rowErr.cost ? "error" : ""}`}
                    value={row.cost}
                    onChange={(e) => updateRow(i, "cost", e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, i, 3)}
                    min="0"
                    step="0.01"
                    disabled={disabled}
                  />
                  {rowErr.cost && <span className="qa-error">{rowErr.cost}</span>}
                </td>
                <td>
                  <input
                    data-row={i}
                    type="date"
                    className={`qa-input ${rowErr.latest_pickup ? "error" : ""}`}
                    value={row.latest_pickup}
                    onChange={(e) => updateRow(i, "latest_pickup", e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, i, 4)}
                    min={today}
                    disabled={disabled}
                  />
                  {rowErr.latest_pickup && <span className="qa-error">{rowErr.latest_pickup}</span>}
                </td>
                <td>
                  <button
                    className="qa-delete-btn"
                    onClick={() => removeRow(i)}
                    disabled={disabled || rows.length <= 1}
                    title={t("Remove row")}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <button className="qa-add-row-btn" onClick={addRow} disabled={disabled}>
        <FontAwesomeIcon icon={faPlus} />
        <span>{t("Add Row")}</span>
      </button>
    </div>
  );
};

QuickAddGrid.propTypes = {
  rows: PropTypes.array.isRequired,
  setRows: PropTypes.func.isRequired,
  materialTypes: PropTypes.array.isRequired,
  errors: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  emptyRow: PropTypes.func.isRequired,
};

export default QuickAddGrid;
